import { Container } from "@mui/material";
import {
  SmartAutoComplete,
  SmartForm,
  SmartTextField,
  useSmartForm,
} from "@nodeon/react-components";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Coordinate,
  PortServiceItemDto,
  PortServiceTypeEnum,
  PortServiceUpdateDto,
} from "~/api/GeneratedApi";

const dataToForm = (
  x: PortServiceItemDto | undefined,
  portId?: number,
): ServiceFormInputs => {
  return {
    portId: x?.port?.id ?? portId ?? null,
    id: x?.id ?? -1,
    description: x?.description ?? "",
    name: x?.name ?? "",
    location: x?.location ?? null,
    availabilityInfo: x?.availabilityInfo ?? "",
    portServiceType:
      x?.portServiceType ??
      (x && "type" in x
        ? (x as unknown as PortServiceUpdateDto).type
        : PortServiceTypeEnum.Other),
  };
};

export interface ServiceFormInputs {
  id: number | null;
  portId: number | null;
  name: string;
  description: string;
  location: Coordinate | null;
  availabilityInfo: string;
  portServiceType: PortServiceTypeEnum;
}

interface Props {
  onSubmit: (data: ServiceFormInputs) => void;
  readOnly?: boolean;
  small?: boolean;
  defaultValues?: PortServiceItemDto;
  formId: string;
  portId?: number;
}

export const ServiceForm = ({
  onSubmit,
  readOnly,
  small,
  formId,
  defaultValues,
  portId,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("port-services");

  const formDataDefaultValues = useMemo(
    () => dataToForm(defaultValues, portId),
    [defaultValues, portId],
  );

  const formMethods = useSmartForm({
    defaultValues: formDataDefaultValues,
    mode: "onSubmit",
  });

  React.useEffect(() => {
    formMethods.register("portId");
  }, [formMethods]);

  React.useEffect(() => {
    console.log("defaultValues: %O", defaultValues);
  }, [defaultValues]);

  React.useEffect(() => {
    console.log("formDataDefaultValues: %O", formDataDefaultValues);
  }, [formDataDefaultValues]);

  const portServiceTypeEnumOptions = useMemo(
    () =>
      Object.keys(PortServiceTypeEnum)
        .filter((k) => Number.isNaN(Number(k)))
        .map((x) => PortServiceTypeEnum[x as unknown as PortServiceTypeEnum])
        .map((x) => parseInt(x, 10)) ?? [],
    [],
  );

  const getPortServiceTypeEnumLabel = useCallback(
    (opt: PortServiceTypeEnum) => {
      return t(
        `service-type-enum-${PortServiceTypeEnum[opt].toLowerCase()}`,
      ).toString();
    },
    [t],
  );

  return (
    <Container maxWidth={false}>
      <SmartForm<ServiceFormInputs>
        onSubmit={onSubmit}
        gridProps={{ justifyContent: "center", direction: "column" }}
        methods={formMethods}
        formId={formId}
      >
        <SmartTextField<ServiceFormInputs>
          name="name"
          label={t("name")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          required
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
          autoFocus
        />
        <SmartTextField<ServiceFormInputs>
          name="description"
          label={t("description")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
        />
        <SmartTextField<ServiceFormInputs>
          name="availabilityInfo"
          label={t("availability-info")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
        />
        <SmartAutoComplete<ServiceFormInputs, number>
          name="portServiceType"
          label={t("service-type")}
          options={portServiceTypeEnumOptions}
          gridProps={{ xs: small ? 11 : 12 }}
          fullWidth
          disabled={readOnly}
          getOptionLabel={getPortServiceTypeEnumLabel}
        />
      </SmartForm>
    </Container>
  );
};
